import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { BulkImportBrand, BulkUserType, EditableBulkRow, InternalPreviewData, PartnerPreviewData } from '../types';
import { formatRowErrors, getBulkRoleLabel, getBulkRowStatusLabel } from '../utils';
import { BulkImportSummary } from './BulkImportSummary';

interface BulkImportReviewProps {
  userType: BulkUserType;
  fileName: string;
  brands: BulkImportBrand[];
  rows: EditableBulkRow[];
  summary: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    removedRows: number;
    sendableRows: number;
  };
  setupError: string;
  isConfirming: boolean;
  onBack: () => void;
  onEdit: (_row: EditableBulkRow) => void;
  onRemove: (_row: EditableBulkRow) => void;
  onConfirm: () => void;
}

export function BulkImportReview({
  userType,
  fileName,
  brands,
  rows,
  summary,
  setupError,
  isConfirming,
  onBack,
  onEdit,
  onRemove,
  onConfirm,
}: BulkImportReviewProps) {
  const visibleRows = rows.filter((row) => !row.removed);
  const isInternal = userType === 'internal';

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">Arquivo</p>
        <p className="text-sm font-medium text-gray-900">{fileName}</p>
      </div>

      <BulkImportSummary
        userType={userType}
        brands={brands}
        totalRows={summary.totalRows}
        validRows={summary.validRows}
        invalidRows={summary.invalidRows}
        removedRows={summary.removedRows}
        sendableRows={summary.sendableRows}
      />

      {visibleRows.length === 0 ? (
        <p className="rounded-md border border-gray-200 p-4 text-sm text-muted-foreground">
          Nenhuma linha disponível para revisão.
        </p>
      ) : (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Linha</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>{isInternal ? 'Role' : 'WhatsApp'}</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Erros</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((row) => {
                  const internalData = row.data as InternalPreviewData;
                  const partnerData = row.data as PartnerPreviewData;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell>{row.data.nome || '-'}</TableCell>
                      <TableCell>{row.data.email || '-'}</TableCell>
                      <TableCell>
                        {isInternal ? getBulkRoleLabel(internalData.role || '-') : partnerData.whatsapp || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={row.status === 'valid' ? 'secondary' : 'destructive'}>
                          {getBulkRowStatusLabel(row.status)}
                        </Badge>
                        {row.edited && <span className="ml-2 text-xs text-muted-foreground">Editada</span>}
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-red-600">
                        {formatRowErrors(row.errors) || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onEdit(row)}
                            disabled={isConfirming}
                            aria-label={`Editar linha ${row.rowNumber}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onRemove(row)}
                            disabled={isConfirming}
                            aria-label={`Remover linha ${row.rowNumber}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-3 md:hidden">
            {visibleRows.map((row) => {
              const internalData = row.data as InternalPreviewData;
              const partnerData = row.data as PartnerPreviewData;
              return (
                <article key={row.id} className="space-y-2 rounded-md border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900">Linha {row.rowNumber}</p>
                    <Badge variant={row.status === 'valid' ? 'secondary' : 'destructive'}>
                      {getBulkRowStatusLabel(row.status)}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-700">Nome: {row.data.nome || '-'}</p>
                  <p className="text-sm text-gray-700">E-mail: {row.data.email || '-'}</p>
                  <p className="text-sm text-gray-700">
                    {isInternal
                      ? `Role: ${getBulkRoleLabel(internalData.role || '-')}`
                      : `WhatsApp: ${partnerData.whatsapp || '-'}`}
                  </p>
                  {row.errors.length > 0 && (
                    <p className="text-sm text-red-600">{formatRowErrors(row.errors)}</p>
                  )}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => onEdit(row)} disabled={isConfirming}>
                      Editar
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => onRemove(row)} disabled={isConfirming}>
                      Remover
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}

      {setupError && (
        <p className="text-sm text-red-600" role="alert">
          {setupError}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onBack} disabled={isConfirming}>
          Selecionar outro arquivo
        </Button>
        <Button type="button" onClick={onConfirm} disabled={isConfirming || summary.sendableRows === 0}>
          {isConfirming ? 'Criando usuários...' : 'Confirmar importação'}
        </Button>
      </div>
    </div>
  );
}
